import { useState, useCallback } from 'react';
import { logger } from '../utils/logger';

/**
 * Custom hook for file handling operations
 * @param {function} processFiles - Function to process uploaded files
 * @param {function} setCompletedSteps - Function to update completed steps
 * @returns {object} File handling functions and state
 */
export const useFileHandling = (processFiles, setCompletedSteps) => {
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [fileDrop, setFileDrop] = useState(0);

  /**
   * Handle file drop
   */
  const onDrop = useCallback((acceptedFiles) => {
    logger.info('[useFileHandling]', 'Files dropped:', acceptedFiles.length);

    // Handle file uploads and separate from text input
    // If reuploading (existing files present), reset previous data and versions
    if (files.length > 0) {
      setCompletedSteps([]);
      setFileDrop(prev => prev + 1); // Increment to trigger re-analysis
    }

    setFiles(acceptedFiles);
    setText(''); // Clear text when files are uploaded
  }, [files.length, setCompletedSteps]);

  /**
   * Handle text input change
   */
  const handleTextChange = useCallback((event) => {
    setText(event.target.value);
    setFiles([]); // Clear files when text is entered
  }, []);

  /**
   * Remove uploaded file
   */
  const handleRemoveFile = useCallback((index) => {
    logger.info('[useFileHandling]', 'Removing file at index:', index);
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);

    // Clear text if all files are removed
    if (newFiles.length === 0) {
      setText('');
    }
  }, [files]);

  /**
   * Process files and text for analysis
   */
  const prepareFilesForAnalysis = useCallback(async () => {
    logger.info('[useFileHandling]', 'Preparing files for analysis');

    let processedData = null;

    if (files.length > 0 && processFiles) {
      // Process uploaded files
      processedData = await processFiles(files);
    } else if (text.trim()) {
      // Use text input
      processedData = { text: text.trim() };
    }

    return processedData;
  }, [files, text, processFiles]);

  /**
   * Check if input is valid (either files or text)
   */
  const hasValidInput = useCallback(() => {
    return (text.trim() !== '' || files.length > 0);
  }, [text, files]);

  /**
   * Get input type (files or text)
   */
  const getInputType = useCallback(() => {
    if (files.length > 0) return 'files';
    if (text.trim()) return 'text';
    return 'none';
  }, [files, text]);

  /**
   * Reset all inputs
   */
  const resetInputs = useCallback(() => {
    setFiles([]);
    setText('');
    setFileDrop(0);
  }, []);

  return {
    files,
    setFiles,
    text,
    setText,
    fileDrop,
    onDrop,
    handleTextChange,
    handleRemoveFile,
    prepareFilesForAnalysis,
    hasValidInput,
    getInputType,
    resetInputs
  };
};
